import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateEffect } from 'react-use';
import { useGraphql } from 'utils/hooks/useGraphql';

import { NewFolderModal } from 'ui/indexPages/modals/NewFolderModal';
import { PAGE_SIZES, PaginationFooter } from 'ui/paginationFooter';
import { FullWidth } from 'ui/utility/fullWidth';

import { CHECKBOX_STATUS } from 'ui/checkbox/checkboxStatus';
import { FolderBreadcrumbs } from 'ui/indexPages/folders/folderBreadcrumbs';
import { MoveToFolderModal } from 'ui/indexPages/modals/MoveToFolderModal';
import { RenameFolderModal } from 'ui/indexPages/modals/RenameFolderModal';
import { UnarchiveItemsModal } from 'ui/indexPages/modals/UnarchiveItemsModal';

import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';

import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import {
  createSerializationOptionsForEnumValue,
  serializationOptionsForNumberValue,
  serializationOptionsForStringArrayValue,
  serializationOptionsForStringValue,
  useSearchParamState,
} from 'utils/hooks/useSearchParamState';

import {
  fetchIndexPageCreatorsAndModifiers,
  fetchIndexPageWorkflowFoldersQuery,
} from 'api/queries/workflowIndex';

import { useProjectIdSearchParamOnMount } from 'modules/app/hooks/useInitializeCurrentProject/useProjectIdSearchParamOnMount';
import {
  AncestorIdData,
  AncestorNameData,
  FolderAncestorsData,
  FolderId,
  FolderIdData,
  FolderNameData,
  splitFolderContents,
} from 'modules/domains/foldering';
import { JourneySubfolder } from 'modules/domains/journey';

import { cancelJourneyCampaignAlerts, rescheduleJourneyCampaignAlerts } from 'api/WorkflowsAPI';
import {
  selectIsCampaignPerformanceAlertsFeatureFlagOn,
  selectIsJourneysCampaignPerformanceAlertsFeatureFlagOn,
} from 'core/userContext/selectors';
import { GRAPHQL_TYPENAMES } from 'modules/graphql/types';
import { JourneyConversion } from 'modules/journey/components/JourneyConversions';
import { IndexSwitchProjectModal } from 'ui/indexPages/modals/IndexSwitchProjectsModal/IndexSwitchProjectsModal';
import { deselectAllItems, selectItems } from './actions';
import { BulkActionBar } from './components/BulkActionsBar';
import { CopyWorkflowDialog } from './components/CopyWorkflowDialog';
import { DeleteItemsDialog } from './components/DeleteItemsDialog';
import { EmptyStateMessage } from './components/EmptyStateMessage';
import { ExportCsvDialog } from './components/ExportCsvDialog';
import { FiltersBar } from './components/FiltersBar';
import { NewWorkflowDialog } from './components/NewWorkflowDialog';
import { NoResultsMessage } from './components/NoResultsMessage';
import { PageContainer } from './components/PageContainer';
import { ToggleWorkflowDialog } from './components/ToggleWorkflowDialog';
import { WorkflowIndexHeader } from './components/WorkflowIndexHeader';
import { WorkflowsTable } from './components/WorkflowsTable';
import {
  DEFAULT_WORKFLOW_CREATORS,
  DEFAULT_WORKFLOW_MODIFIERS,
  GENERAL_ERROR_MESSAGE,
  QUERY_PARAMS,
  SELECTABLE_ITEM_TYPE,
  TABLE_COLUMNS,
  TOGGLE_MODAL_TYPE,
  UNARCHIVE_MODAL_DISPLAY_TEXT,
  WORKFLOW_CONTENT_ITEM_NAME,
} from './constants';
import { useArchiveWorkflowsAndDeleteFolders } from './hooks/useArchiveWorkflowsAndDeleteFolders';
import { useCreateWorkflowFolder } from './hooks/useCreateWorkflowFolder';
import { useIds } from './hooks/useIds';
import { useCreateNewJourney } from './hooks/useCreateNewJourney';
import { useMoveToFolder } from './hooks/useMoveToFolder';
import { useRenameFolder } from './hooks/useRenameFolder';
import { useRootFolderId } from './hooks/useRootFolderId';
import { useSendActivityForWorkflows } from './hooks/useSendActivityForWorkflows';
import { useTreeViewQuery } from './hooks/useTreeViewQuery';
import { useWorkflowsQueryVariables } from './hooks/useWorkflowsQueryVariables';
import { selectIsJourneyLabelsEnabled, selectSelectedItemsByType } from './selectors';
import {
  GraphQLWorkflow,
  GraphQLWorkflowCreatorsModifiersQueryResponse,
  Workflow,
  WorkflowDescriptionData,
  WorkflowId,
  WorkflowNameData,
  WorkflowType,
} from './types';
import { cloneWorkflow } from './utils/cloneWorkflow';
import { createDraftWorkflow } from './utils/createDraftWorkflow';
import { exportWorkflowsToCsv } from './utils/exportWorkflowsToCsv';
import { toggleWorkflows } from './utils/toggleWorkflows';
import { unarchiveWorkflows } from './utils/unarchiveWorkflows';
import { JourneyUsageWarningBanner } from './components/JourneyUsageWarningBanner';

const { useState, useCallback, useEffect, useMemo } = React;

export const WorkflowIndexPage = () => {
  const dispatch = useDispatch();
  const ROOT_FOLDER_ID = useRootFolderId();
  useProjectIdSearchParamOnMount();

  /**
   * Feature flags
   */
  const isCampaignPerformanceAlertsEnabled = useSelector(
    selectIsCampaignPerformanceAlertsFeatureFlagOn,
  );
  const isJourneysCampaignPerformanceAlertsEnabled = useSelector(
    selectIsJourneysCampaignPerformanceAlertsFeatureFlagOn,
  );

  const isJourneyLabelsEnabled = useSelector(selectIsJourneyLabelsEnabled);
  /**
   * Folder State
   */
  const [folderIdSearchParam] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.FOLDER_ID,
    stateValueWhenSearchParamIsUnset: ROOT_FOLDER_ID,
    ...serializationOptionsForNumberValue,
  });

  /**
   * CSV Export Dialog State + Callbacks
   */
  const [isCsvExportDialogShowing, setIsCsvExportDialogShowing] = useState(false);
  const showCsvExportDialog = useCallback(() => setIsCsvExportDialogShowing(true), []);
  const hideCsvExportDialog = useCallback(() => setIsCsvExportDialogShowing(false), []);
  const handleCsvExportConfirmation = useCallback(() => {
    hideCsvExportDialog();
    exportWorkflowsToCsv(dispatch);
  }, [hideCsvExportDialog, dispatch]);

  /**
   * Pagination State + Callbacks
   */
  const [currentPage, setCurrentPage] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.PAGE,
    stateValueWhenSearchParamIsUnset: 1,
    ...serializationOptionsForNumberValue,
  });
  const [currentPageSize, setCurrentPageSize] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.PAGE_SIZE,
    stateValueWhenSearchParamIsUnset: PAGE_SIZES[1],
    ...serializationOptionsForNumberValue,
  });
  const handlePaginationUpdate = useCallback(({ page, pageSize }: any) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
  }, []);

  /**
   * Search State + Callbacks
   */
  const [search, setSearch] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.SEARCH,
    stateValueWhenSearchParamIsUnset: '',
    ...serializationOptionsForStringValue,
  });

  /**
   * Creator/Modifier Query State
   */
  const creatorModifierQueryResult = useGraphql<GraphQLWorkflowCreatorsModifiersQueryResponse>(
    fetchIndexPageCreatorsAndModifiers,
  );
  const {
    workflowCreators = DEFAULT_WORKFLOW_CREATORS,
    workflowModifiers = DEFAULT_WORKFLOW_MODIFIERS,
  } = creatorModifierQueryResult.data || {};
  const creatorAndModifierUsersById = useMemo(
    () =>
      [...(workflowCreators ?? []), ...(workflowModifiers ?? [])].reduce(
        (previousValue, user) => ({ ...previousValue, [user.id]: user }),
        {},
      ),
    [workflowCreators, workflowModifiers],
  );

  /**
   * Filter State
   */
  const [statusFilterIds, setStatusFilterIds] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.STATUS,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });
  const [workflowTypeFilterIds, setWorkflowTypeFilterIds] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.WORKFLOW_TYPE,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });
  const [triggerTypeFilterIds, setTriggerTypeFilterIds] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.TRIGGER_TYPE,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });
  const [creatorFilterIds, setCreatorFilterIds] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.CREATED_BY,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });
  const [modifierFilterIds, setModifierFilterIds] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.UPDATED_BY,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });

  const [labelFilterIds, setLabelFilterIds] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.LABELS,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });
  const [journeyVisitorTrackingIds, setJourneyVisitorTrackingIds] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.JOURNEY_VISITOR_TRACKING,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });
  const clearFilters = useCallback(() => {
    setStatusFilterIds([]);
    setWorkflowTypeFilterIds([]);
    setTriggerTypeFilterIds([]);
    setCreatorFilterIds([]);
    setModifierFilterIds([]);
    setLabelFilterIds([]);
  }, []);
  const canClear =
    !!statusFilterIds.length ||
    !!workflowTypeFilterIds.length ||
    !!triggerTypeFilterIds.length ||
    !!creatorFilterIds.length ||
    !!modifierFilterIds.length ||
    !!labelFilterIds.length;
  useUpdateEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    statusFilterIds,
    workflowTypeFilterIds,
    triggerTypeFilterIds,
    creatorFilterIds,
    modifierFilterIds,
    labelFilterIds,
    journeyVisitorTrackingIds,
  ]);

  /**
   * Sorting State
   */
  const [sortedBy, setSortedBy] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.SORT_BY,
    stateValueWhenSearchParamIsUnset: TABLE_COLUMNS.LAST_MODIFIED,
    ...createSerializationOptionsForEnumValue({
      stateValueWhenSearchParamIsInvalid: TABLE_COLUMNS.LAST_MODIFIED,
      enumKeyValueMap: TABLE_COLUMNS,
    }),
  });
  const [sortDirection, setSortDirection] = useSearchParamState({
    searchParamKey: QUERY_PARAMS.SORT_DIRECTION,
    stateValueWhenSearchParamIsUnset: SORT_DIRECTIONS.DESCENDING,
    ...createSerializationOptionsForEnumValue({
      stateValueWhenSearchParamIsInvalid: SORT_DIRECTIONS.DESCENDING,
      enumKeyValueMap: SORT_DIRECTIONS,
    }),
  });

  /**
   * Workflow Query State
   */
  const variables = useWorkflowsQueryVariables({
    currentPage,
    currentPageSize,
    statusFilterIds,
    workflowTypeFilterIds,
    triggerTypeFilterIds,
    search,
    sortedBy,
    sortDirection,
    creatorFilterIds,
    modifierFilterIds,
    folderId: folderIdSearchParam,
    labelFilterIds,
    journeyVisitorTrackingIds,
  });
  const result = useGraphql(fetchIndexPageWorkflowFoldersQuery, {
    variables,
    onError: () => {
      dispatch(
        addNegativeToastWithErrorId(GENERAL_ERROR_MESSAGE, 'fetchIndexPageWorkflowFoldersQuery'),
      );
    },
    // This causes a bug when there are duplicate IDs
    // https://github.com/apollographql/apollo-client/issues/9169
    // notifyOnNetworkStatusChange: true,

    /**
     * Caching is temporarily disabled while the Workflow Index Page is using the
     * REST API to archive/unarchive/enable/disable workflows. Once these operations
     * are refactored to use GraphQL mutations, the cache can be re-enabled.
     */
    fetchPolicy: 'no-cache',
  });

  let folder: FolderIdData &
    FolderAncestorsData<AncestorIdData & AncestorNameData> &
    FolderNameData = {
    id: ROOT_FOLDER_ID,
    name: '',
    ancestors: [],
  };
  let workflows: Workflow[] = [];
  let folders: JourneySubfolder[] = [];
  let totalItemCount = 0;
  [folders = [], workflows = []] = splitFolderContents<JourneySubfolder, GraphQLWorkflow>(
    result.data?.workflowFolder?.content || [],
    GRAPHQL_TYPENAMES.JOURNEY,
  );
  totalItemCount = result.data?.workflowFolder?.info.count || 0;
  folder = result.data?.workflowFolder || {
    id: ROOT_FOLDER_ID,
    name: '',
    ancestors: [],
  };

  const isLoading = result.isLoading || result.networkStatus < 7;
  const isResultFiltered = !!(
    statusFilterIds.length ||
    triggerTypeFilterIds.length ||
    search.length
  );
  const { refetch: refetchWorkflows } = result;
  const folderAncestorsForBreadcrumbs = useMemo(
    () => [...folder.ancestors, { id: folder.id, name: folder.name }],
    [folder],
  );

  /**
   * Workflow Send Activity State
   */
  useSendActivityForWorkflows(workflows);

  /**
   * Selection State + Callbacks
   */
  const selectedWorkflowIds = useSelector(selectSelectedItemsByType(SELECTABLE_ITEM_TYPE.WORKFLOW));
  const selectedWorkflowDraftIds = useSelector(
    selectSelectedItemsByType(SELECTABLE_ITEM_TYPE.WORKFLOW_DRAFT),
  );
  const selectedFolderIds = useSelector(
    selectSelectedItemsByType(SELECTABLE_ITEM_TYPE.WORKFLOW_FOLDER),
  );
  const isWorkflowDraft = (workflowType: WorkflowType = WorkflowType.PUBLISHED) =>
    workflowType === WorkflowType.DRAFT;

  // Memoized selection callbacks
  const deselectAll = useCallback(() => {
    dispatch(deselectAllItems());
  }, [dispatch]);
  const selectAll = useCallback(() => {
    dispatch(
      selectItems([
        ...workflows.map(({ id, workflowType }) => ({
          id,
          type: isWorkflowDraft(workflowType)
            ? SELECTABLE_ITEM_TYPE.WORKFLOW_DRAFT
            : SELECTABLE_ITEM_TYPE.WORKFLOW,
        })),

        ...folders.map(({ id }) => ({ id, type: SELECTABLE_ITEM_TYPE.WORKFLOW_FOLDER })),
      ]),
    );
  }, [dispatch, workflows, folders]);

  // Computed group selection state
  const selectedWorkflows = workflows.filter(({ id, workflowType }) =>
    isWorkflowDraft(workflowType) ? selectedWorkflowDraftIds.has(id) : selectedWorkflowIds.has(id),
  );
  const selectedFolders = folders.filter(({ id }) => selectedFolderIds.has(id));
  const totalSelectedItems = selectedWorkflows.length + selectedFolders.length;
  const totalSelectableItems = workflows.length + folders.length;
  let groupSelectStatus = CHECKBOX_STATUS.INDETERMINATE;
  if (totalSelectedItems === 0) {
    groupSelectStatus = CHECKBOX_STATUS.UNCHECKED;
  } else if (totalSelectedItems === totalSelectableItems) {
    groupSelectStatus = CHECKBOX_STATUS.CHECKED;
  }

  // Effect to deselect all each time the user navigates between folders
  useEffect(deselectAll, [folder.id]);

  /**
   * Creation Dialog State + Callbacks
   */
  const [isCreationDialogShowing, setIsCreationDialogShowing] = useState(false);
  const showCreationDialog = useCallback(() => setIsCreationDialogShowing(true), []);
  const hideCreationDialog = useCallback(() => setIsCreationDialogShowing(false), []);
  const createNewJourney = useCreateNewJourney();
  const handleCreationConfirmation = useCallback(
    ({
      name,
      description,
      conversions,
      labels,
    }: WorkflowNameData &
      WorkflowDescriptionData & { conversions: JourneyConversion[]; labels?: string[] }) => {
      hideCreationDialog();

      if (isJourneyLabelsEnabled) {
        createNewJourney({
          name,
          description,
          destinationFolderId: folder.id,
          conversions,
          labels,
        });
      } else {
        createDraftWorkflow(name, description, folder.id, conversions, dispatch, true);
      }
    },
    [hideCreationDialog, folder.id],
  );

  /**
   * Deletion Dialog State + Callbacks
   */
  const [foldersBeingDeleted, setFoldersBeingDeleted] = useState<JourneySubfolder[]>([]);
  const [workflowsBeingArchived, setWorkflowsBeingArchived] = useState<Workflow[]>([]);
  const [workflowsBeingDeleted, setWorkflowsBeingDeleted] = useState<Workflow[]>([]);
  const showDeletionDialogForFolder = useCallback(
    (folderId: FolderId) => {
      const folderToDelete = folders.find(({ id }) => id === folderId);
      if (folderToDelete) {
        setFoldersBeingDeleted([folderToDelete]);
      }
    },
    [folders],
  );
  const showArchiveDialogForWorkflow = useCallback(
    (workflowId: WorkflowId) => {
      const workflowToArchive = workflows.find(({ id }) => id === workflowId);
      if (workflowToArchive) {
        setWorkflowsBeingArchived([workflowToArchive]);
      }
    },
    [workflows],
  );
  const showDeleteDialogForWorkflow = useCallback(
    (workflowId: WorkflowId) => {
      const workflowToDelete = workflows.find(({ id }) => id === workflowId);
      if (workflowToDelete) {
        setWorkflowsBeingDeleted([workflowToDelete]);
      }
    },
    [workflows],
  );
  const showDeletionDialogForSelection = useCallback(() => {
    setFoldersBeingDeleted(selectedFolders);
    if (selectedWorkflows.some((workflow) => isWorkflowDraft(workflow.workflowType))) {
      setWorkflowsBeingDeleted(
        selectedWorkflows.filter(({ workflowType }) => isWorkflowDraft(workflowType)),
      );
      setWorkflowsBeingArchived(
        selectedWorkflows.filter(
          ({ workflowType }) => !workflowType || workflowType !== WorkflowType.DRAFT,
        ),
      );
    } else {
      setWorkflowsBeingArchived(selectedWorkflows);
    }
  }, [selectedWorkflows, selectedFolders]);
  const hideDeletionDialog = useCallback(() => {
    setFoldersBeingDeleted([]);
    setWorkflowsBeingArchived([]);
    setWorkflowsBeingDeleted([]);
  }, []);
  const archiveWorkflowsAndDeleteFolders = useArchiveWorkflowsAndDeleteFolders();
  const handleDeletionConfirmation = useCallback(async () => {
    hideDeletionDialog();

    await archiveWorkflowsAndDeleteFolders(
      foldersBeingDeleted,
      workflowsBeingArchived,
      workflowsBeingDeleted,
    );

    deselectAll();
    refetchWorkflows();
  }, [
    archiveWorkflowsAndDeleteFolders,
    workflowsBeingArchived,
    workflowsBeingDeleted,
    foldersBeingDeleted,
    refetchWorkflows,
    deselectAll,
  ]);

  /**
   * Unarchive Dialog State + Callbacks
   */
  const [workflowsBeingUnarchived, setWorkflowsBeingUnarchived] = useState<Workflow[]>([]);
  const showUnarchiveDialogForWorkflow = useCallback(
    (workflowId: WorkflowId) => {
      const workflowToUnarchive = workflows.find(({ id }) => id === workflowId);
      if (workflowToUnarchive) {
        setWorkflowsBeingUnarchived([workflowToUnarchive]);
      }
    },
    [workflows],
  );
  const showUnarchiveDialogForSelection = useCallback(() => {
    setWorkflowsBeingUnarchived(selectedWorkflows);
  }, [selectedWorkflows]);
  const hideUnarchiveDialog = useCallback(() => {
    setWorkflowsBeingUnarchived([]);
  }, []);
  const handleUnarchiveConfirmation = useCallback(() => {
    unarchiveWorkflows(workflowsBeingUnarchived, dispatch).then(() => {
      refetchWorkflows();
      deselectAll();
    });
    hideUnarchiveDialog();
  }, [hideUnarchiveDialog, workflowsBeingUnarchived, refetchWorkflows, deselectAll]);

  /**
   * Toggle Dialog State + Callbacks
   */
  const [workflowsBeingToggled, setWorkflowsBeingToggled] = useState<Workflow[]>([]);
  const [workflowToggleType, setWorkflowToggleType] = useState(TOGGLE_MODAL_TYPE.ENABLE);
  const showEnableDialogForSelection = useCallback(() => {
    setWorkflowsBeingToggled(selectedWorkflows);
    setWorkflowToggleType(TOGGLE_MODAL_TYPE.ENABLE);
  }, [selectedWorkflows]);
  const showDisableDialogForSelection = useCallback(() => {
    setWorkflowsBeingToggled(selectedWorkflows);
    setWorkflowToggleType(TOGGLE_MODAL_TYPE.DISABLE);
  }, [selectedWorkflows]);
  const showEnableDialogForWorkflow = useCallback(
    (workflowId: WorkflowId) => {
      const workflowBeingEnabled = workflows.find(({ id }) => id === workflowId);
      if (workflowBeingEnabled) {
        setWorkflowsBeingToggled([workflowBeingEnabled]);
        setWorkflowToggleType(TOGGLE_MODAL_TYPE.ENABLE);
      }
    },
    [workflows],
  );
  const showDisableDialogForWorkflow = useCallback(
    (workflowId: WorkflowId) => {
      const workflowBeingDisabled = workflows.find(({ id }) => id === workflowId);
      if (workflowBeingDisabled) {
        setWorkflowsBeingToggled([workflowBeingDisabled]);
        setWorkflowToggleType(TOGGLE_MODAL_TYPE.DISABLE);
      }
    },
    [workflows],
  );
  const hideToggleDialog = useCallback(() => {
    setWorkflowsBeingToggled([]);
  }, []);
  const handleToggleConfirmation = useCallback(() => {
    toggleWorkflows(workflowsBeingToggled, workflowToggleType, dispatch).then(() => {
      const isAlertsFeatureEnabled =
        isCampaignPerformanceAlertsEnabled && isJourneysCampaignPerformanceAlertsEnabled;

      if (isAlertsFeatureEnabled) {
        const shouldCancelCampaignAlerts = workflowToggleType === TOGGLE_MODAL_TYPE.DISABLE;
        const shouldRescheduleCampaignAlerts = workflowToggleType === TOGGLE_MODAL_TYPE.ENABLE;
        const workflowIds = workflowsBeingToggled.map(({ id }) => id);

        if (shouldCancelCampaignAlerts) {
          cancelJourneyCampaignAlerts(workflowIds);
        } else if (shouldRescheduleCampaignAlerts) {
          rescheduleJourneyCampaignAlerts(workflowIds);
        }
      }

      refetchWorkflows();
      deselectAll();
    });
    hideToggleDialog();
  }, [
    dispatch,
    hideToggleDialog,
    workflowsBeingToggled,
    workflowToggleType,
    refetchWorkflows,
    deselectAll,
    isCampaignPerformanceAlertsEnabled,
    isJourneysCampaignPerformanceAlertsEnabled,
  ]);

  /**
   * Copy Dialog State + Callbacks
   */
  const [workflowBeingCopied, setWorkflowBeingCopied] = useState<Workflow | null>(null);
  const showCopyDialogForWorkflow = useCallback(
    (workflowId: WorkflowId) => {
      const workflowToCopy = workflows.find(({ id }) => id === workflowId);
      if (workflowToCopy) {
        setWorkflowBeingCopied(workflowToCopy);
      }
    },
    [workflows],
  );
  const hideCopyDialog = useCallback(() => setWorkflowBeingCopied(null), []);
  const handleCopyConfirmation = useCallback(
    (workflow: Workflow, name: string) => {
      hideCopyDialog();
      cloneWorkflow(workflow, folder.id, dispatch, name).then(() => {
        refetchWorkflows();
        deselectAll();
      });
    },
    [hideCopyDialog, refetchWorkflows, deselectAll, folder.id],
  );

  /**
   * Move Dialog State + Callbacks
   */
  const [workflowsBeingMoved, setWorkflowsBeingMoved] = useState<Workflow[]>([]);
  const [foldersBeingMoved, setFoldersBeingMoved] = useState<JourneySubfolder[]>([]);
  const showMoveToFolderDialogForSelection = useCallback(() => {
    setWorkflowsBeingMoved(selectedWorkflows);
    setFoldersBeingMoved(selectedFolders);
  }, [selectedWorkflows, selectedFolders]);
  const showMoveToFolderDialogForFolder = useCallback(
    (folderId: any) => {
      const folderToMove = folders.find(({ id }) => id === folderId);
      if (folderToMove) {
        setWorkflowsBeingMoved([]);
        setFoldersBeingMoved([folderToMove]);
      }
    },
    [folders],
  );
  const showMoveToFolderDialogForWorkflow = useCallback(
    (workflowId: any) => {
      const workflowsToMove = workflows.find(({ id }) => id === workflowId);
      if (workflowsToMove) {
        setFoldersBeingMoved([]);
        setWorkflowsBeingMoved([workflowsToMove]);
      }
    },
    [workflows],
  );
  const moveToFolder = useMoveToFolder();
  const hideMoveToFolderDialog = useCallback(() => {
    setWorkflowsBeingMoved([]);
    setFoldersBeingMoved([]);
  }, []);
  const handleMoveToFolderConfirmation = useCallback(
    async (
      folderIds: FolderId[],
      workflowIds: WorkflowId[],
      destination: AncestorIdData & AncestorNameData,
    ) => {
      deselectAll();
      hideMoveToFolderDialog();
      await moveToFolder(foldersBeingMoved, workflowsBeingMoved, destination);
      refetchWorkflows();
    },
    [
      deselectAll,
      hideMoveToFolderDialog,
      moveToFolder,
      workflowsBeingMoved,
      foldersBeingMoved,
      refetchWorkflows,
    ],
  );
  const workflowsBeingMovedIds = useIds(workflowsBeingMoved);
  const foldersBeingMovedIds = useIds(foldersBeingMoved);

  /**
   * Folder Creation Dialog State + Callbacks
   */
  const [parentFolderIdForNewFolderDialog, setParentFolderIdForNewFolderDialog] =
    useState<FolderId | null>(null);
  const showCreateFolderDialog = useCallback(
    () => setParentFolderIdForNewFolderDialog(folder.id),
    [folder.id],
  );
  const hideCreateFolderDialog = useCallback(() => setParentFolderIdForNewFolderDialog(null), []);
  const createWorkflowFolder = useCreateWorkflowFolder();
  const handleCreateFolderConfirmation = useCallback(
    async (newFolderName: string, newFolderDestination: AncestorIdData & AncestorNameData) => {
      hideCreateFolderDialog();
      await createWorkflowFolder(newFolderName, newFolderDestination);
      refetchWorkflows();
    },
    [createWorkflowFolder, hideCreateFolderDialog, refetchWorkflows],
  );

  /**
   * Folder Rename Dialog State + Callbacks
   */
  const [folderBeingRenamed, setFolderBeingRenamed] = useState<JourneySubfolder | null>(null);
  const showRenameFolderDialogForFolder = useCallback(
    (folderId: FolderId) => {
      const folderToRename = folders.find(({ id }) => id === folderId);
      if (folderToRename) {
        setFolderBeingRenamed(folderToRename);
      }
    },
    [folders],
  );
  const hideRenameFolderDialog = useCallback(() => setFolderBeingRenamed(null), []);
  const renameFolder = useRenameFolder();
  const handleRenameFolderConfirmation = useCallback(
    async (newName: string) => {
      hideRenameFolderDialog();
      if (folderBeingRenamed) {
        await renameFolder(folderBeingRenamed, newName);
        refetchWorkflows();
      }
    },
    [hideRenameFolderDialog, refetchWorkflows, renameFolder, folderBeingRenamed],
  );

  const [showIndexSwitchProjectModal, setShowIndexSwitchProjectModal] = useState(false);
  const [newWorkflowId, setNewWorkflowId] = useState<number | null>(null);
  const [newProjectId, setNewProjectId] = useState<number | null>(null);
  const handleSwitchToProjectJourney = useCallback((workflowId: any, projectId: any) => {
    setShowIndexSwitchProjectModal(true);
    setNewWorkflowId(workflowId);
    setNewProjectId(projectId);
  }, []);
  const hideSwitchToProjectJourneyModal = useCallback(() => {
    setShowIndexSwitchProjectModal(false);
  }, []);

  /**
   * Constants for Conditional Rendering
   */
  const shouldShowEmptyStateMessage = !isLoading && totalItemCount === 0 && !isResultFiltered;
  const shouldShowNoResultsMessage = !isLoading && totalItemCount === 0 && isResultFiltered;
  const shouldShowPaginationControls = !shouldShowEmptyStateMessage;

  return (
    <PageContainer data-test="workflow-index-page">
      <JourneyUsageWarningBanner />
      <WorkflowIndexHeader
        onCreationButtonClick={showCreationDialog}
        onExportCsvButtonClick={showCsvExportDialog}
      />
      {isCreationDialogShowing && (
        <NewWorkflowDialog
          onClose={hideCreationDialog}
          onConfirm={handleCreationConfirmation as any}
        />
      )}
      {parentFolderIdForNewFolderDialog !== null && (
        <NewFolderModal
          onClose={hideCreateFolderDialog}
          onConfirm={handleCreateFolderConfirmation}
          rootFolderId={ROOT_FOLDER_ID}
          defaultDestinationFolderId={parentFolderIdForNewFolderDialog}
          treeViewQuery={useTreeViewQuery}
        />
      )}
      {folderBeingRenamed && (
        <RenameFolderModal
          folderName={folderBeingRenamed.name}
          onClose={hideRenameFolderDialog}
          onConfirm={handleRenameFolderConfirmation}
        />
      )}
      {!!workflowBeingCopied && (
        <CopyWorkflowDialog
          onClose={hideCopyDialog}
          onConfirm={handleCopyConfirmation}
          workflow={workflowBeingCopied}
          handleSwitchToProjectJourney={handleSwitchToProjectJourney}
        />
      )}
      {showIndexSwitchProjectModal && newWorkflowId && newProjectId && (
        <IndexSwitchProjectModal
          workflowId={newWorkflowId}
          projectId={newProjectId}
          onClose={hideSwitchToProjectJourneyModal}
          isOpen
        />
      )}
      {isCsvExportDialogShowing && (
        <ExportCsvDialog onClose={hideCsvExportDialog} onConfirm={handleCsvExportConfirmation} />
      )}
      {!!(
        foldersBeingDeleted.length +
        workflowsBeingArchived.length +
        workflowsBeingDeleted.length
      ) && (
        <DeleteItemsDialog
          onClose={hideDeletionDialog}
          onConfirm={handleDeletionConfirmation}
          workflowsBeingDeleted={workflowsBeingDeleted}
          workflowsBeingArchived={workflowsBeingArchived}
          foldersBeingDeleted={foldersBeingDeleted}
        />
      )}
      {!!(foldersBeingMoved.length + workflowsBeingMoved.length) && (
        <MoveToFolderModal
          contentIds={workflowsBeingMovedIds}
          folderIds={foldersBeingMovedIds}
          contentItemName={WORKFLOW_CONTENT_ITEM_NAME}
          onConfirm={handleMoveToFolderConfirmation}
          onClose={hideMoveToFolderDialog}
          currentFolderId={folder.id}
          rootFolderId={ROOT_FOLDER_ID}
          treeViewQuery={useTreeViewQuery}
        />
      )}
      {!!workflowsBeingUnarchived.length && (
        <UnarchiveItemsModal
          onClose={hideUnarchiveDialog}
          onConfirm={handleUnarchiveConfirmation}
          displayTextForContentItems={UNARCHIVE_MODAL_DISPLAY_TEXT}
          content={workflowsBeingUnarchived}
        />
      )}
      {!!workflowsBeingToggled.length && (
        <ToggleWorkflowDialog
          onClose={hideToggleDialog}
          onConfirm={handleToggleConfirmation}
          workflows={workflowsBeingToggled}
          toggleType={workflowToggleType}
        />
      )}
      {totalSelectedItems && !isLoading ? (
        <FullWidth>
          <BulkActionBar
            selectedWorkflows={selectedWorkflows}
            selectedFolders={selectedFolders}
            onEnableButtonClick={showEnableDialogForSelection}
            onDisableButtonClick={showDisableDialogForSelection}
            onArchiveOrDeleteButtonClick={showDeletionDialogForSelection}
            onUnarchiveButtonClick={showUnarchiveDialogForSelection}
            onMoveToFolderButtonClick={showMoveToFolderDialogForSelection}
          />
        </FullWidth>
      ) : (
        <FiltersBar
          search={search}
          setSearch={setSearch}
          onClear={clearFilters}
          canClear={canClear}
          statusFilterIds={statusFilterIds}
          setStatusFilterIds={setStatusFilterIds}
          setWorkflowTypeFilterIds={setWorkflowTypeFilterIds}
          workflowTypeFilterIds={workflowTypeFilterIds}
          triggerTypeFilterIds={triggerTypeFilterIds}
          setTriggerTypeFilterIds={setTriggerTypeFilterIds}
          workflowCreators={workflowCreators}
          workflowModifiers={workflowModifiers}
          creatorFilterIds={creatorFilterIds}
          setCreatorFilterIds={setCreatorFilterIds}
          modifierFilterIds={modifierFilterIds}
          setModifierFilterIds={setModifierFilterIds}
          labelFiltersIds={labelFilterIds}
          setLabelFilterIds={setLabelFilterIds}
          onCreateFolderButtonClick={showCreateFolderDialog}
          journeyVisitorTrackingIds={journeyVisitorTrackingIds}
          setJourneyVisitorTrackingIds={setJourneyVisitorTrackingIds}
        />
      )}
      <FullWidth>
        {folder.id !== ROOT_FOLDER_ID && (
          <FolderBreadcrumbs
            folderAncestors={folderAncestorsForBreadcrumbs}
            rootFolderId={ROOT_FOLDER_ID}
          />
        )}
        <WorkflowsTable
          selectAll={selectAll}
          deselectAll={deselectAll}
          groupSelectStatus={groupSelectStatus}
          sortedBy={sortedBy}
          setSortedBy={setSortedBy}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          workflows={workflows}
          creatorAndModifierUsersById={creatorAndModifierUsersById}
          onArchiveWorkflowOptionClick={showArchiveDialogForWorkflow}
          onUnarchiveWorkflowOptionClick={showUnarchiveDialogForWorkflow}
          onCopyWorkflowOptionClick={showCopyDialogForWorkflow}
          onDeleteWorkflowFolderOptionClick={showDeletionDialogForFolder}
          onDeleteWorkflowOptionClick={showDeleteDialogForWorkflow}
          onEnableWorkflowOptionClick={showEnableDialogForWorkflow}
          onDisableWorkflowOptionClick={showDisableDialogForWorkflow}
          onMoveWorkflowFolderOptionClick={showMoveToFolderDialogForFolder}
          onMoveWorkflowOptionClick={showMoveToFolderDialogForWorkflow}
          onCreateWorkflowFolderOptionClick={setParentFolderIdForNewFolderDialog}
          onRenameWorkflowFolderOptionClick={showRenameFolderDialogForFolder}
          folders={folders}
          isLoading={isLoading}
        />
        {shouldShowNoResultsMessage && <NoResultsMessage />}
        {shouldShowEmptyStateMessage && (
          <EmptyStateMessage onCreateWorkflowButtonClick={showCreationDialog} />
        )}
        {shouldShowPaginationControls && (
          <PaginationFooter
            currentPage={currentPage}
            itemsPerPage={currentPageSize}
            numItems={totalItemCount}
            onUpdate={handlePaginationUpdate}
            pageSizes={PAGE_SIZES}
          />
        )}
      </FullWidth>
    </PageContainer>
  );
};
